import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { TreesIcon, UsersIcon, ScaleIcon, ScanLineIcon, GlobeIcon, ShieldCheckIcon } from 'lucide-react';
export function CoffeeFeatures() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-27">

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-28">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-29">

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-30">
            The real cost of{' '}
            <span className="text-carbon-bright" data-id="element-31">untraceable coffee</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-deep max-w-2xl mx-auto" data-id="element-32">
            Without traceability, the coffee industry perpetuates environmental
            destruction, labor exploitation, and consumer deception.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-33">

          {/* Card 1 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-34">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-35">
              <TreesIcon className="w-7 h-7 text-carbon-bright" data-id="element-36" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-37">
              Deforestation
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-38">
              Coffee is the 5th largest driver of deforestation globally.
              Without origin tracking, illegally cleared land enters supply
              chains undetected, destroying biodiversity hotspots in Brazil,
              Vietnam, and Indonesia.
            </p>
          </motion.article>

          {/* Card 2 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-39">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-40">
              <UsersIcon className="w-7 h-7 text-carbon-bright" data-id="element-41" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-42">
              Labor Exploitation
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-43">
              An estimated 1.5 million children work in coffee production.
              Traceability ensures fair wages, safe conditions, and eliminates
              forced labor from certified supply chains.
            </p>
          </motion.article>

          {/* Card 3 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-44">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-45">
              <ScaleIcon className="w-7 h-7 text-carbon-bright" data-id="element-46" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-47">
              Price Inequality
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-48">
              Farmers receive only 7-10% of the retail price. Transparent
              traceability enables direct trade models where growers earn 2-3x
              more per kilogram.
            </p>
          </motion.article>

          {/* Card 4 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-49">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-50">
              <GlobeIcon className="w-7 h-7 text-carbon-bright" data-id="element-51" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-52">
              Carbon Footprint
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-53">
              A single cup of coffee generates 0.4 kg of CO₂. With 2.25 billion
              cups consumed daily, traceability is essential to measure and
              reduce coffee's 270M+ ton annual carbon impact.
            </p>
          </motion.article>

          {/* Card 5 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-54">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-55">
              <ScanLineIcon className="w-7 h-7 text-carbon-bright" data-id="element-56" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-57">
              Consumer Demand
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-58">
              73% of millennials are willing to pay more for sustainably sourced
              coffee. Brands with verified traceability see 20-35% higher
              customer loyalty and premium pricing power.
            </p>
          </motion.article>

          {/* Card 6 */}
          <motion.article className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-59">

            <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-60">
              <ShieldCheckIcon className="w-7 h-7 text-carbon-bright" data-id="element-61" />
            </div>
            <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-62">
              EU Regulation
            </h3>
            <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-63">
              The EU Deforestation Regulation (EUDR) requires full traceability
              for coffee imports by 2025. Non-compliance means market access
              loss for the EU's €10B+ coffee import market.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}